import React from 'react';

interface AlertProps {
    children: React.ReactNode;
    variant?: 'info' | 'success' | 'warning' | 'danger';
    title?: string;
    dismissible?: boolean;
    onDismiss?: () => void;
    className?: string;
    icon?: React.ReactNode;
    actions?: React.ReactNode;
}

const Alert: React.FC<AlertProps> = ({
    children,
    variant = 'info',
    title,
    dismissible = false,
    onDismiss,
    className = '',
    icon,
    actions
}) => {
    const variants = {
        info: {
            container: 'bg-[var(--info-light)] border-[var(--info)]/20 text-[var(--text-primary)]',
            accent: 'bg-[var(--info)]',
            icon: 'text-[var(--info)]',
            defaultIcon: (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
            )
        },
        success: {
            container: 'bg-[var(--success-light)] border-[var(--success)]/20 text-[var(--text-primary)]',
            accent: 'bg-[var(--success)]',
            icon: 'text-[var(--success)]',
            defaultIcon: (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
            )
        },
        warning: {
            container: 'bg-[var(--warning-light)] border-[var(--warning)]/20 text-[var(--text-primary)]',
            accent: 'bg-[var(--warning)]',
            icon: 'text-[var(--warning)]',
            defaultIcon: (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
            )
        },
        danger: {
            container: 'bg-[var(--danger-light)] border-[var(--danger)]/20 text-[var(--text-primary)]',
            accent: 'bg-[var(--danger)]',
            icon: 'text-[var(--danger)]',
            defaultIcon: (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
            )
        }
    };

    const currentVariant = variants[variant];

    return (
        <div className={`
            relative overflow-hidden border rounded-[var(--radius-lg)] p-4.5
            transition-all duration-[var(--duration-normal)] ease-[var(--ease-calm)]
            ${currentVariant.container}
            ${className}
        `}>
            {/* Left accent bar */}
            <div className={`absolute left-0 top-0 bottom-0 w-[3px] rounded-l-[var(--radius-lg)] ${currentVariant.accent}`} />

            <div className="flex items-start pl-2.5">
                <div className={`flex-shrink-0 mt-0.5 ${currentVariant.icon}`}>
                    {icon || currentVariant.defaultIcon}
                </div>

                <div className="ml-3.5 flex-1 min-w-0">
                    {title && (
                        <h3 className="text-[var(--font-body)] font-bold mb-1.5 leading-tight text-[var(--text-heading)] tracking-[-0.01em]">
                            {title}
                        </h3>
                    )}
                    <div className="text-[13px] leading-relaxed text-[var(--text-secondary)]">
                        {children}
                    </div>
                    {actions && (
                        <div className="mt-3.5 flex items-center gap-2.5">
                            {actions}
                        </div>
                    )}
                </div>

                {dismissible && (
                    <div className="flex-shrink-0 ml-4">
                        <button
                            onClick={onDismiss}
                            className={`
                                inline-flex rounded-[var(--radius-sm)] p-1.5
                                transition-all duration-[var(--duration-fast)]
                                text-[var(--text-muted)] hover:text-[var(--text-primary)]
                                hover:bg-[var(--bg-hover)]
                                focus:outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--primary-500)]/20
                            `}
                        >
                            <span className="sr-only">Dismiss</span>
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Alert;
